import { ChartIcon, HomeIcon, LockIcon, SearchIcon, SettingsIcon, TeamIcon } from "@signet/ui";
import type { Needs } from "@signet/web-kit";
import { webModules } from "../../../../modules/registry.web.ts";
import type { QuickPlace } from "../quick/search.ts";
import { USAGE_NEEDS } from "../pages/usage/needs.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Signet's own places, outside any module: the shell's bar and the desktop
   quick bar both list them, so they are kept here once.
   ────────────────────────────────────────────────────────────────────────── */

// `needs` as a list opens the place to anyone holding one of them. `keywords`
// are extra words the palette matches it on.
export type Place = { to: string; label: string; icon: React.ComponentType<{ size?: number }>; needs: Needs | null; keywords?: string };

export const CORE_PLACES: Place[] = [
  { to: "/", label: "Now", icon: HomeIcon, needs: "signet.now:view" },
];
export const ADMIN_PLACES: Place[] = [
  { to: "/people", label: "People", icon: TeamIcon, needs: "signet.people:view" },
  { to: "/roles", label: "Roles", icon: LockIcon, needs: "signet.roles:view" },
  // Research's HikerAPI spend joined it on 2026-10-01, so it opens for anyone
  // who could see either page; the words cover what each used to be found by.
  {
    to: "/usage",
    label: "API/AI Usage",
    icon: ChartIcon,
    needs: USAGE_NEEDS,
    keywords: "ai usage api usage claude max anthropic railway hosting mulerouter hikerapi cost spend billing balance budget limits tokens",
  },
];

/** The icon of whichever tab a page sits under, for the quick bar's rows. */
export function placeIcon(to: string): React.ComponentType<{ size?: number }> {
  if (to === "/settings") return SettingsIcon;
  const own = [...CORE_PLACES, ...ADMIN_PLACES].find((p) => p.to === to);
  if (own) return own.icon;
  const module = webModules.find((m) => m.nav && !("soon" in m.nav) && (to === m.nav.to || to.startsWith(`${m.nav.to}/`)));
  return module?.nav && !("soon" in module.nav) ? module.nav.icon : SearchIcon;
}

/**
 * Everything the quick bar may open by name for this person: every tab
 * (Now, each module's place in the bar, the admin pages) and every page a
 * module offers in the palette. Only what they can open, so it never offers
 * a page that would answer "you can't".
 */
export function quickPlaces(can: (needs: Needs) => boolean): QuickPlace[] {
  const tabs: Place[] = [
    ...CORE_PLACES,
    ...webModules.flatMap((m): Place[] => (m.nav && !("soon" in m.nav) ? [{ to: m.nav.to, label: m.nav.label, icon: m.nav.icon, needs: m.nav.needs }] : [])),
    ...ADMIN_PLACES,
  ];
  const seen = new Set<string>();
  const places: QuickPlace[] = [];
  for (const p of tabs) {
    if (p.needs && !can(p.needs)) continue;
    seen.add(p.to);
    places.push({ id: `go:${p.to}`, label: p.label, to: p.to, kind: "tab", keywords: p.keywords });
  }
  for (const m of webModules) {
    for (const c of m.commands ?? []) {
      // A command that is only the tab again ("Links" at /links) would be a second row for the same page.
      if (!can(c.needs) || (seen.has(c.to) && places.some((p) => p.to === c.to && p.label === c.label))) continue;
      places.push({ id: c.id, label: c.label, to: c.to, kind: "page", hint: c.hint, keywords: c.keywords });
    }
  }
  places.push({ id: "settings", label: "Settings", to: "/settings", kind: "page", hint: "Your account", keywords: "password theme account preferences" });
  return places;
}
